import { motion, useReducedMotion } from 'motion/react';
import { cn } from '@/lib/cn';

/**
 * Decorative geometric motif inspired by the Ankyah triangle mark: three chevron strokes forming a
 * triangle, plus drifting outlined triangles. Purely decorative (aria-hidden), transform/opacity only.
 */
export function TriangleMotif({ className }: { className?: string }) {
  const reduce = useReducedMotion();
  const floaters = [
    { x: '8%', y: '14%', size: 90, delay: 0, rotate: 12, duration: 14 },
    { x: '72%', y: '8%', size: 56, delay: 1.2, rotate: -18, duration: 11 },
    { x: '80%', y: '62%', size: 120, delay: 0.6, rotate: 32, duration: 16 },
    { x: '18%', y: '70%', size: 64, delay: 2, rotate: -8, duration: 12 },
    { x: '46%', y: '38%', size: 36, delay: 0.3, rotate: 50, duration: 10 },
  ];
  return (
    <div aria-hidden className={cn('pointer-events-none absolute inset-0 overflow-hidden', className)}>
      {floaters.map((f, i) => (
        <motion.svg
          key={i}
          viewBox="0 0 100 100"
          className="absolute"
          style={{ left: f.x, top: f.y, width: f.size, height: f.size }}
          initial={{ opacity: 0, scale: 0.8, rotate: f.rotate }}
          animate={
            reduce
              ? { opacity: 0.5, scale: 1, rotate: f.rotate }
              : {
                  opacity: [0.25, 0.6, 0.25],
                  scale: 1,
                  rotate: [f.rotate, f.rotate + 12, f.rotate],
                  y: [0, -16, 0],
                }
          }
          transition={{ duration: f.duration, delay: f.delay, repeat: Infinity, ease: 'easeInOut' }}
        >
          <polygon
            points="50,6 94,86 6,86"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
          />
        </motion.svg>
      ))}
    </div>
  );
}

/** The mark redrawn as three animated chevrons (used as a large hero illustration). */
export function AnimatedMark({ className }: { className?: string }) {
  const reduce = useReducedMotion();
  const segments = [
    { d: 'M78 20 L44 80 L64 80 L88 38 Z', fill: '#19E3E3' },
    { d: 'M100 52 L132 108 L120 128 L90 74 Z', fill: '#00C2C2' },
    { d: 'M28 100 L100 100 L112 120 L16 120 Z', fill: '#009798' },
  ];
  return (
    <svg viewBox="0 0 148 140" className={className} aria-hidden>
      {segments.map((s, i) => (
        <motion.path
          key={i}
          d={s.d}
          fill={s.fill}
          initial={reduce ? false : { opacity: 0, scale: 0.6, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ delay: 0.15 + i * 0.12, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          style={{ transformOrigin: '74px 74px' }}
        />
      ))}
    </svg>
  );
}
